import { Button, CloseButton, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closeInnerPopup,
  openInnerPopup,
} from '../../data/providers/popupsProvider';
import {
  comparisonPopulationSettingsProvider,
  IComparisonPopulationSetting,
} from '../../data/providers/settings/comparisonPopulationSettingsProvider';
import { EZScorePreference } from '../../data/types';
import { setZscorePreference } from '../../data/writers/settings';
import { Column } from '../layout/Column';
import { CardBody } from '../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../shared/card/CardHeader';
import { ControlSpacer } from '../shared/form/ControlSpacer';
import { FormContainer } from '../shared/form/FormContainer';
import { SelectBox } from '../shared/form/SelectBox';
import { InnerPopupCard, PopupCardContent } from '../shared/popups/PopupCard';

interface IPopupProps {
  onClose: () => void;
}

export const ZScorePopup = (props: IPopupProps): React.ReactElement => {
  const [preference, setPreference] = React.useState<EZScorePreference>(
    EZScorePreference.Unscaled
  );

  /**
   * @description subscribe to the users z score setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = comparisonPopulationSettingsProvider().subscribe(
      (comparison: IComparisonPopulationSetting): void =>
        setPreference(comparison.zScorePreference)
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description handles scaled selection
   * @param e
   */
  const onScaled = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!!e.currentTarget.checked) {
      setZscorePreference(EZScorePreference.Scaled);
    }
  };

  /**
   * @description handles unscaled selection
   * @param e
   */
  const onUnscaled = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!!e.currentTarget.checked) {
      setZscorePreference(EZScorePreference.Unscaled);
    }
  };

  return (
    <InnerPopupCard onClose={props.onClose} style={{ minWidth: '300px' }}>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h5>ZScore Settings</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          <FormContainer>
            <Column>
              <ControlSpacer>
                <SelectBox
                  label='Unscaled (-4 to 4)'
                  selected={preference === EZScorePreference.Unscaled}
                  onSelect={onUnscaled}
                />
              </ControlSpacer>
              <ControlSpacer>
                <SelectBox
                  label='Scaled (0 to 100)'
                  selected={preference === EZScorePreference.Scaled}
                  onSelect={onScaled}
                />
              </ControlSpacer>
            </Column>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <Button onClick={props.onClose} title='Close' />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};

export const ZScoreSettings = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const showPopup = (): void =>
      openInnerPopup(
        <ZScorePopup onClose={closeInnerPopup(props.history)} />,
        props.history,
        'zscore'
      );

    return (
      <IconButton icon='settings' onClick={showPopup} title='ZScore Settings' />
    );
  }
);
